<script lang="ts">
	import { transformInfiniteDataNfts } from '../../../../utils/transformInfiniteDataNfts';
	import { getArtistSectionCount } from '../../utils/getArtistSectionCount';
	import { setArtistSectionCount } from '../../utils/setArtistSectionCount';
	import { ProjectSectionTitle } from '../ProjectSectionTitle';
	import { infiniteApiQuery } from '#lib/api/infiniteApiQuery/infiniteApiQuery.js';
	import { GlobalRoutes } from '#lib/features/navigation/constants/global-routes.js';
	import { NftCollectionCard } from '#lib/features/nfts/components/NftCollectionCard/index.js';
	import { gqlClient } from '#lib/gqlClient.js';
	import {
		artistSectionQueryKey,
		getArtistNfts,
	} from '#lib/websites/artists/api/getArtistSections.js';
	import {
		ARTIST_PROJECTS_LIMIT,
		NFTS_SORT,
	} from '#lib/websites/artists/pages/ArtistDetailPage/utils/artistDetailsPageLoad/artistDetailsPageLoad.js';
	import { GetArtistPageNftsDocument } from '#lib/websites/artists/queries/__generated__/getArtistPageNfts.generated.js';
	import { page } from '$app/state';
	import { Button } from '$global/components/Button';
	import { getImageUrl } from '$global/utils/getImageUrl';
	import { getInfiniteData } from '$global/utils/infinite-loading/getInfiniteData';
	import { hasMoreInfiniteItems } from '$global/utils/infinite-loading/hasMoreInfiniteItems';
	import { infiniteQuery } from '$global/utils/infinite-loading/infiniteQuery';
	import { shouldRestoreInfiniteItems } from '$global/utils/infinite-loading/shouldRestoreInfiniteItems';
	import type { ArtistsPageData } from '$routes/artists/[slug]/types';

	interface Props {
		dataCy: string;
	}

	let { dataCy }: Props = $props();
	let dataCyPrefix = $derived(`${dataCy}-nfts`);
	let artist = $derived(page.data.artist as ArtistsPageData['artist']);
	let variables = $derived(page.data.variables as ArtistsPageData['variables']);
	let count = $derived(artist.totalNfts?.count || 0);

	// Branches on the data the route's own loader produced rather than on the
	// flag: this component is shared, and only the loader knows which path ran.
	let useRest = $derived('restArtistSlug' in page.data);
	let slug = $derived((page.data.restArtistSlug ?? '') as string);

	// The REST page is stored in the same envelope the GraphQL page uses, so the
	// transform, getPageRows and getInfiniteData below stay shared across both
	// paths.
	let query = $derived(
		useRest
			? infiniteApiQuery({
					queryKey: artistSectionQueryKey('nfts', slug, {
						limit: ARTIST_PROJECTS_LIMIT,
						offset: 0,
					}),
					queryFn: async (offset: number) => ({
						Artists: [
							{
								nft_collections: (
									await getArtistNfts(undefined, slug, {
										limit: ARTIST_PROJECTS_LIMIT,
										offset,
									})
								).items,
							},
						],
					}),
					limit: ARTIST_PROJECTS_LIMIT,
					maxItems: count,
				})
			: infiniteQuery({
					gqlClient,
					variables: { ...variables, sort: NFTS_SORT },
					document: GetArtistPageNftsDocument,
					limit: ARTIST_PROJECTS_LIMIT,
				})
	);

	let nfts = $derived(
		getInfiniteData({
			query,
			transform: transformInfiniteDataNfts,
		})
	);

	// Use server-side data immediately, fall back to infinite query data once loaded
	let displayItems = $derived(
		nfts && nfts.length > 0
			? nfts
			: transformInfiniteDataNfts({
					Artists: [{ nft_collections: artist.nft_collections }],
				})
	);

	let hasMore = $derived(
		hasMoreInfiniteItems({
			query,
			totalCount: count,
			loadedCount: displayItems?.length || 0,
			limit: ARTIST_PROJECTS_LIMIT,
			getPageRows: (queryPage) => queryPage.Artists[0]?.nft_collections,
		})
	);

	const handleLoadMoreNfts = () => {
		setArtistSectionCount(
			'nfts',
			(displayItems?.length || 0) + ARTIST_PROJECTS_LIMIT
		);
		query.fetchNextPage();
	};

	// After back/forward navigation the TanStack cache usually still holds the
	// previously loaded pages; when it has been garbage-collected, catch back
	// up to the count persisted in the history entry page by page.
	$effect(() => {
		if (
			shouldRestoreInfiniteItems({
				query,
				targetCount: getArtistSectionCount('nfts'),
				loadedCount: displayItems?.length || 0,
			})
		) {
			query.fetchNextPage();
		}
	});
</script>

{#if displayItems && displayItems.length > 0}
	<div class="border-b border-gray-200"></div>
	<div>
		<ProjectSectionTitle
			title="NFT"
			href={GlobalRoutes.NftHome}
			dataCy={dataCyPrefix}
			label="View all NFTs"
		/>
		<div
			class="mb-8 grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3 md:gap-6 lg:grid-cols-4 lg:gap-8"
		>
			{#each displayItems as collection}
				<NftCollectionCard
					dataCy={dataCyPrefix}
					nftCollection={{
						artist: collection?.artist?.name ?? '',
						featured_image:
							getImageUrl(collection?.featured_image?.filename_disk) ?? '',
						slug: collection?.slug ?? '',
						title: collection?.title ?? '',
					}}
				/>
			{/each}
		</div>
		{#if hasMore}
			<Button
				dataCy={`${dataCyPrefix}-load-more`}
				variant="secondary"
				size="md"
				class="mx-auto mb-8 w-full md:w-fit"
				loading={query.isFetchingNextPage}
				onclick={handleLoadMoreNfts}>Show more</Button
			>
		{/if}
	</div>
{/if}
